import {type CSSProperties, useEffect, useMemo, useState} from 'react'
import {View} from 'react-native'
import {AtUri} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {EMBED_SERVICE} from '#/lib/constants'
import {atoms as a} from '#/alf'
import {useSemanticTokens} from '#/wsocial/service/design-system'

/** Before the iframe reports its real height. */
const MIN_HEIGHT = 180

/**
 * Live preview of how the post will look once embedded, shown inside the Embed
 * dialog.
 *
 * Rather than re-implementing the card, this frames the real embed service —
 * the same thing a third-party site gets — so the preview can't drift from the
 * actual output. The embed renderer posts `{id, height}` up to its parent as it
 * lays out (see `wsocialembed/src/components/container.tsx`); we match on `id`
 * and size the frame to it, which is exactly what `snippet/embed.ts` does on a
 * real page.
 */
export function EmbedPreview({
  postUri,
  colorMode,
}: {
  postUri: string
  colorMode: 'system' | 'light' | 'dark'
}) {
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const [height, setHeight] = useState(MIN_HEIGHT)

  // Identifies height messages meant for this frame specifically.
  const id = useMemo(() => String(Math.random()).slice(2), [])

  const src = useMemo(() => {
    const urip = new AtUri(postUri)
    const params = new URLSearchParams()
    params.set('id', id)
    params.set('colorMode', colorMode)
    /*
     * `/embed/<did>/<collection>/<rkey>` — the path `screens/post.tsx` reads
     * back into an at-uri.
     */
    return `${EMBED_SERVICE}/embed/${urip.host}/${urip.collection}/${urip.rkey}?${params.toString()}`
  }, [postUri, colorMode, id])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== EMBED_SERVICE) return
      const data = event.data as {id?: string; height?: number} | null
      if (!data || data.id !== id || !data.height) return
      setHeight(Math.max(MIN_HEIGHT, data.height))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [id])

  return (
    <View
      style={[
        a.w_full,
        a.overflow_hidden,
        /*
         * Must match the outer `rounded-[24px]` of the embed container
         * (wsocialembed/src/components/container.tsx) - a smaller clip radius
         * leaves this wrapper background showing in the corners.
         */
        {borderRadius: 24, backgroundColor: ss.bg.contrast_25},
      ]}>
      <iframe
        // Remount on src change so a theme switch re-renders at the new mode
        // instead of showing the previous one until it reloads.
        key={src}
        src={src}
        title={_(msg`Embed preview`)}
        width="100%"
        height={height}
        scrolling="no"
        style={iframeStyle}
      />
    </View>
  )
}

// Defined outside the component to avoid recreation on every render.
const iframeStyle: CSSProperties = {
  border: 'none',
  display: 'block',
  colorScheme: 'normal',
}
